import { Icon } from '@/components/ui/icon';
import { FiltersAlphabetical } from '@/components/shared/filters/alphabetical';
import { useClientTranslation } from '@/lib/core';
import { PreferenceEntry } from './index';

// ─── PreferenceItemRow ────────────────────────────────────────────────────────

interface PreferenceItemRowProps {
  name: string;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

function PreferenceItemRow({ name, isSelected, isDisabled, onToggle }: PreferenceItemRowProps) {
  const t = useClientTranslation();

  if (isDisabled) {
    return (
      <div className="border-jncquoi-neutral-200 flex h-[57px] w-full items-center gap-4 border-b py-6">
        <Icon name="bookmark" width={16} height={16} className="text-jncquoi-neutral-700 shrink-0" />
        <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[12px] tracking-[0.5px] uppercase">
          {name}
        </span>
        <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase">
          {t('App.Labels.MembersOnly')}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="border-jncquoi-neutral-200 flex h-[57px] w-full items-center gap-4 border-b py-6 text-left"
      onClick={onToggle}
    >
      <Icon
        name={isSelected ? 'bookmark-solid' : 'bookmark'}
        width={24}
        height={24}
        className="text-jncquoi-neutral-1000 shrink-0"
      />
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
        {name}
      </span>
    </button>
  );
}

// ─── ProfilePreferencesList ───────────────────────────────────────────────────

export interface ProfilePreferencesListProps {
  items: PreferenceEntry[];
  /** When true, items are grouped by first letter with letter dividers and a sticky side A–Z index. */
  alphabetical?: boolean;
  onToggle: (id: string, isSelected: boolean) => void;
}

export function ProfilePreferencesList({
  items,
  alphabetical = false,
  onToggle,
}: ProfilePreferencesListProps) {
  if (!alphabetical) {
    return (
      <div className="flex flex-col">
        {items.map(item => (
          <PreferenceItemRow
            key={item.id}
            name={item.name}
            isSelected={item.isSelected}
            isDisabled={item.isDisabled}
            onToggle={() => onToggle(item.id, item.isSelected)}
          />
        ))}
      </div>
    );
  }

  return (
    <FiltersAlphabetical items={items} getLabel={item => item.name}>
      {letterItems => (
        <div className="flex flex-col">
          {letterItems.map(item => (
            <PreferenceItemRow
              key={item.id}
              name={item.name}
              isSelected={item.isSelected}
              isDisabled={item.isDisabled}
              onToggle={() => onToggle(item.id, item.isSelected)}
            />
          ))}
        </div>
      )}
    </FiltersAlphabetical>
  );
}
